import noop from 'lodash/noop';
import { useEffect, useState } from 'react';
import useStaticCallback from 'hooks/useStaticCallback';
import { DrawableAudioPlayer } from 'utils/audio/types';

export interface UseWaveformPreviewConfig {
  enabled: boolean;
  onPause?: () => void;
  waveformPlayer: DrawableAudioPlayer | undefined;
}

export interface UseWaveformPreviewResult {
  isPlaying: boolean;
}

export default function useWaveformPreview({
  enabled,
  onPause = noop,
  waveformPlayer,
}: UseWaveformPreviewConfig) {
  const [isPlaying, setIsPlaying] = useState(enabled);
  const staticOnPause = useStaticCallback(onPause);

  // registers callbacks with the preview player in order to keep isPlaying in
  // sync with the player
  useEffect(() => {
    if (!waveformPlayer) return;

    const handlePause = () => {
      setIsPlaying(false);
      staticOnPause();
    };
    const handlePlay = () => setIsPlaying(true);

    waveformPlayer.on('pause', handlePause);
    waveformPlayer.on('play', handlePlay);

    // new player, so set isPlaying accordingly
    setIsPlaying(!!waveformPlayer.paused);

    return () => {
      waveformPlayer.off('pause', handlePause);
      waveformPlayer.off('play', handlePlay);
    };
  }, [staticOnPause, waveformPlayer]);

  // stop playing the preview if `enabled` becomes false, start playing it if
  // `enabled` becomes true
  useEffect(() => {
    if (waveformPlayer) {
      if (!enabled && !waveformPlayer.paused) {
        waveformPlayer.pause();
      } else if (enabled && waveformPlayer.paused) {
        waveformPlayer.play();
      }
    }
  }, [enabled, waveformPlayer]);

  return { isPlaying };
}
